/**
 * Elements Service
 *
 * API (OpenAPI): https://pe.api.qa-wpcmodx.com/swagger/doc.json
 *
 * - POST   …/elements/add     — body: **array** of StowedElement
 * - PUT    …/elements/update  — body: CheckpointPayload ({ StowedElements: StowedElement[] }, single or batch)
 * - PUT    …/elements/move    — body: array of MoveElementDto ({ objectId, x, y })
 * - PUT    …/elements/resize  — body: array of ResizeElementDto ({ objectId, x, y, width, height, rotation? })
 * - PUT    …/elements/rotate  — body: array of RotateElementDto
 * - DELETE …/elements/delete/{objectIds} — comma-separated IDs
 * - GET    …/elements/get/{objectIds}
 */

import { httpClient } from '@/api/http/client'
import { KIND_TO_FIELD } from '@/api/types/stowed-element'
import type {
  CheckpointPayload,
  ElementLayer,
  LinkedTextElement,
  PageElement,
  StowedElement,
} from '@/api/types/stowed-element'
import { fractionalIndexForStowedObject } from '@/lib/elements/stowed-fractional-index'
import {
  type RotateElementDto,
  type RotateElementDtoResponse,
  rotateElementDtosFromStowedElements,
} from '@/api/types/rotate-element'

export type { StowedElement, ElementLayer } from '@/api/types/stowed-element'

/** Success payloads vary by route; update often returns StowedElement[] per Swagger. */
export type ElementWriteResponse = { message?: string } | StowedElement[]

export type ElementDeleteResponse = {
  message?: string
}

export type ElementsByIdsResponse = Record<string, unknown>

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

/** Per-call write options for element mutations. */
export interface ElementWriteOptions {
  /**
   * Forwarded to `fetch`'s `keepalive` option so the request survives page unload — needed when
   * flushing from `beforeunload`/`pagehide` (see `ChangeQueue.flush`), where a plain in-flight
   * fetch can be aborted by the browser before it completes.
   */
  keepalive?: boolean
}

function toRequestConfig(options?: ElementWriteOptions): { keepalive?: boolean } | undefined {
  return options?.keepalive ? { keepalive: true } : undefined
}

function layerPath(spreadId: string, layer: ElementLayer): string {
  return `/checkpoints/${enc(spreadId)}/layers/${enc(layer)}/elements`
}

/** API validates `fractionalIndex` on full StowedElement writes. */
function normalizeStowedElementForApi(body: StowedElement): StowedElement {
  const nested =
    body.elements !== undefined ? body.elements.map(normalizeStowedElementForApi) : undefined

  return {
    ...body,
    fractionalIndex: fractionalIndexForStowedObject(body.objectId, body.fractionalIndex),
    ...(nested !== undefined ? { elements: nested } : {}),
  }
}

/**
 * The API rejects a batch (add/update/move/resize/rotate) whose StowedElements aren't in strictly
 * ascending `fractionalIndex` order — e.g. `{"field":"fractionalIndex","message":"FractionalIndex
 * values must be in strictly ascending order"}`. Callers build this array in whatever order their
 * own operation naturally produces (selection order, enqueue/seq order, etc.), which has no
 * reason to match z-order, so sort here — the one seam every batched write passes through —
 * rather than requiring every call site to remember to pre-sort.
 */
function normalizeStowedElementsForApi(bodies: StowedElement[]): StowedElement[] {
  return bodies.map(normalizeStowedElementForApi).sort((a, b) => {
    const fiA = a.fractionalIndex ?? ''
    const fiB = b.fractionalIndex ?? ''

    // Plain ordinal comparison, not localeCompare: fractionalIndex uses a fixed
    // 0-9/A-Z/a-z alphabet (see FI_DIGITS in stowed-fractional-index.ts) whose ordering must
    // match UTF-16 code unit order, not locale-sensitive collation.
    return fiA < fiB ? -1 : fiA > fiB ? 1 : 0
  })
}

/**
 * Create one or more elements.
 * POST …/elements/add — request body is an **array** of StowedElement (client-generated objectId per item).
 */
async function addElement(
  spreadId: string,
  layer: ElementLayer,
  body: CheckpointPayload,
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.post<CheckpointPayload, ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/add`,
    {
      StowedElements: normalizeStowedElementsForApi(body.StowedElements),
      linkedTextElements: body.linkedTextElements,
    },
    toRequestConfig(options)
  )
}

/**
 * Create a pen-tool shape element.
 * POST …/elements/pen/add — request body is a **single** StowedElement (not an array).
 */
async function addPenElement(
  spreadId: string,
  layer: ElementLayer,
  body: StowedElement,
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.post<StowedElement, ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/pen/add`,
    normalizeStowedElementForApi(body),
    toRequestConfig(options)
  )
}

/**
 * Update an existing element (full replacement).
 * PUT …/elements/update — request body is CheckpointPayload ({ StowedElements: StowedElement[] }).
 */
async function updateElement(
  spreadId: string,
  layer: ElementLayer,
  objectId: string,
  body: StowedElement,
  linkedTextElements?: LinkedTextElement,
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.put<CheckpointPayload, ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/update`,
    {
      StowedElements: [normalizeStowedElementForApi({ ...body, objectId })],
      ...(linkedTextElements ? { linkedTextElements } : {}),
    },
    toRequestConfig(options)
  )
}

/**
 * Batch update existing elements (full element replacement).
 * PUT …/elements/update — same route as single update; body is CheckpointPayload.
 */
async function updateElements(
  spreadId: string,
  layer: ElementLayer,
  bodies: StowedElement[],
  linkedTextElements?: LinkedTextElement,
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.put<CheckpointPayload, ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/update`,
    {
      StowedElements: normalizeStowedElementsForApi(bodies),
      ...(linkedTextElements ? { linkedTextElements } : {}),
    },
    toRequestConfig(options)
  )
}

/** Move request contract: per-element stubs of objectId plus the new base x/y. */
export interface MoveElementDto {
  objectId: string
  x?: number
  y?: number
}

/**
 * The move endpoint rejects payloads missing x/y rather than defaulting them, so an element
 * without a resolvable base is sent without coordinates and fails validation server-side
 * instead of silently moving to (0,0).
 */
function toMoveElementDto(body: StowedElement): MoveElementDto {
  const field = KIND_TO_FIELD[body.kindId]
  const kindBody = field ? (body[field] as { base?: PageElement } | undefined) : undefined

  return { objectId: body.objectId, x: kindBody?.base?.x, y: kindBody?.base?.y }
}

/**
 * Move one or more elements (batch update of x/y per API contract).
 * PUT /checkpoints/{spreadId}/layers/{layerName}/elements/move
 */
async function moveElement(
  spreadId: string,
  layer: ElementLayer,
  bodies: StowedElement[],
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.put<MoveElementDto[], ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/move`,
    bodies.map(toMoveElementDto),
    toRequestConfig(options)
  )
}

/** Resize request contract: per-element stubs of objectId plus the new base frame. */
export interface ResizeElementDto {
  objectId: string
  x?: number
  y?: number
  width?: number
  height?: number
  /** Only sent when the base carries it; an omitted rotation preserves the cached value. */
  rotation?: number
}

/**
 * The resize endpoint rejects payloads missing x/y/width/height rather than defaulting them, so
 * an element without a resolvable base is sent without its frame and fails validation
 * server-side instead of silently collapsing to zero.
 */
function toResizeElementDto(body: StowedElement): ResizeElementDto {
  const field = KIND_TO_FIELD[body.kindId]
  const kindBody = field ? (body[field] as { base?: PageElement } | undefined) : undefined
  const base = kindBody?.base

  return {
    objectId: body.objectId,
    x: base?.x,
    y: base?.y,
    width: base?.width,
    height: base?.height,
    ...(base?.rotation !== undefined ? { rotation: base.rotation } : {}),
  }
}

/**
 * Resize one or more elements.
 * PUT /checkpoints/{spreadId}/layers/{layerName}/elements/resize
 */
async function resizeElement(
  spreadId: string,
  layer: ElementLayer,
  bodies: StowedElement[],
  options?: ElementWriteOptions
): Promise<ElementWriteResponse> {
  return await httpClient.put<ResizeElementDto[], ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/resize`,
    bodies.map(toResizeElementDto),
    toRequestConfig(options)
  )
}

/**
 * Rotate one or more elements (batch rotation / associated base fields per API contract).
 * PUT /checkpoints/{spreadId}/layers/{layerName}/elements/rotate
 */
async function rotateElement(
  spreadId: string,
  layer: ElementLayer,
  bodies: StowedElement[],
  options?: ElementWriteOptions
): Promise<RotateElementDtoResponse[]> {
  const rotateDtos = rotateElementDtosFromStowedElements(bodies)

  return await httpClient.put<RotateElementDto[], RotateElementDtoResponse[]>(
    `${layerPath(spreadId, layer)}/rotate`,
    rotateDtos,
    toRequestConfig(options)
  )
}

/**
 * The ids ride in the URL path, and the API edge answers 414 once the request line passes about
 * 16 KB (roughly 430 ids), so Select All on a large spread never reached the backend. Each request
 * carries at most this many characters of ids, well under that limit and under 8 KB proxy defaults.
 */
const MAX_DELETE_IDS_CHARS = 6000

/** Splits already-encoded ids into groups whose comma-joined length stays within the budget. */
function chunkEncodedIds(encodedIds: string[]): string[][] {
  const chunks: string[][] = []
  let current: string[] = []
  let currentChars = 0

  for (const id of encodedIds) {
    const added = id.length + (current.length > 0 ? 1 : 0)

    if (current.length > 0 && currentChars + added > MAX_DELETE_IDS_CHARS) {
      chunks.push(current)
      current = []
      currentChars = 0
    }

    currentChars += current.length > 0 ? id.length + 1 : id.length
    current.push(id)
  }

  if (current.length > 0) chunks.push(current)

  return chunks
}

/**
 * Delete one or more elements by objectId.
 * DELETE /checkpoints/{spreadId}/layers/{layerName}/elements/delete/{objectId[,objectId...]}
 * Large lists are sent as several sequential requests; the first failure rejects and stops the rest.
 */
async function deleteElements(
  spreadId: string,
  layer: ElementLayer,
  objectIds: string[],
  options?: ElementWriteOptions
): Promise<ElementDeleteResponse> {
  if (objectIds.length === 0) return {}

  let response: ElementDeleteResponse = {}

  for (const chunk of chunkEncodedIds(objectIds.map((id) => enc(id)))) {
    response = await httpClient.delete<ElementDeleteResponse>(
      `${layerPath(spreadId, layer)}/delete/${chunk.join(',')}`,
      toRequestConfig(options)
    )
  }

  return response
}

async function deleteElement(
  spreadId: string,
  layer: ElementLayer,
  objectId: string,
  options?: ElementWriteOptions
): Promise<ElementDeleteResponse> {
  return await deleteElements(spreadId, layer, [objectId], options)
}

/**
 * Resequence elements by updating their fractionalIndex values in bulk.
 * PUT /checkpoints/{spreadId}/layers/{layerName}/elements/resequence
 * Body: array of element stubs — only objectId and fractionalIndex are used.
 */
async function resequenceElements(
  spreadId: string,
  layer: ElementLayer,
  bodies: StowedElement[]
): Promise<ElementWriteResponse> {
  return await httpClient.put<StowedElement[], ElementWriteResponse>(
    `${layerPath(spreadId, layer)}/resequence`,
    normalizeStowedElementsForApi(bodies)
  )
}

/**
 * Fetch element data by object IDs from the API.
 * GET /checkpoints/{spreadId}/layers/{layerName}/elements/get/{objectIds}
 */
async function getElementsByIds(
  spreadId: string,
  layerName: ElementLayer,
  objectIds: string[]
): Promise<ElementsByIdsResponse> {
  if (objectIds.length === 0) return {}

  const ids = objectIds.map((id) => enc(id)).join(',')

  return await httpClient.get<ElementsByIdsResponse>(`${layerPath(spreadId, layerName)}/get/${ids}`)
}

export const elementsService = {
  addElement,
  addPenElement,
  updateElement,
  updateElements,
  moveElement,
  resizeElement,
  rotateElement,
  resequenceElements,
  deleteElement,
  deleteElements,
  getElementsByIds,
}
